import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@domestika-dev/prudencio/dist/atoms/Buttons";
import { DomestikaSimpleLiveCard } from "@domestika-dev/prudencio/dist/molecules/DomestikaSimpleLiveCard";
import { IconForm } from "@domestika-dev/prudencio/dist/atoms/Icon";
import { ArrowRight } from "@domestika-dev/prudencio/dist/atoms/Icons";
import { Flex, Grid, GridItem } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { Loader } from "@domestika-dev/prudencio/dist/atoms/Loader";
import { Text } from "@domestika-dev/prudencio/dist/atoms/Typography";
import { EmptySectionPlaceholder } from "./EmptySectionPlaceholder";
import When from "../../../../common/When";
import { LiveSessionContext } from "../../context/LiveSessionContext";
import { useLanguagesList } from "../../customHooks/useLanguagesList";

const getRoundIndex = (index) => index + 1;

const loadingPreviousLivesItems = Array(4).fill({ loading: true });

export const PreviousLives = () => {
  const {
    courseLoading,
    userLoading,
    previousLivesLoading,
    hasActiveSession,
    isTeacher,
    liveSessionsList,
    liveSessionsListNextPage,
    isLastLiveSessionsListPage,
    onScheduleNew,
    onRequestNextPage,
    onEnterLiveRecording,
  } = useContext(LiveSessionContext);

  const intl = useIntl();
  const [previousLivesList, setPreviousLivesList] = useState(loadingPreviousLivesItems);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoaderIntersecting, setLoaderIntersecting] = useState();
  const observer = useRef(null);

  const { getLanguagesList } = useLanguagesList();
  const languagesList = useRef(getLanguagesList());

  const getLanguageLabel = useCallback(
    (language) => languagesList.current?.find(({ value }) => value === language)?.label,
    [languagesList]
  );

  const loaderRef = (node) => {
    if (node !== null) {
      if (observer?.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver(([entry]) => {
        setLoaderIntersecting(entry?.isIntersecting);
      });
      observer.current.observe(node);
    }
  };

  const isInitialDataLoading = () =>
    courseLoading || userLoading || (previousLivesLoading && !liveSessionsList?.length);

  useEffect(() => {
    return () => {
      observer?.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    let listItems = previousLivesList;
    if (isInitialDataLoading()) {
      listItems = loadingPreviousLivesItems;
    } else if (!previousLivesLoading) {
      if (liveSessionsList?.length) {
        const mappedItems = liveSessionsList.map(
          ({ id, formattedStartedAt, formattedDuration, language, title }) => ({
            liveInfo: {
              scheduledAt: formattedStartedAt,
              duration: formattedDuration,
              language: getLanguageLabel(language),
              title,
            },
            onClick: () => onEnterLiveRecording(id),
          })
        );

        listItems = mappedItems;
      } else if (previousLivesList?.length) {
        listItems = [];
      }
    }

    setPreviousLivesList(listItems);
  }, [courseLoading, previousLivesLoading, userLoading]);

  useEffect(() => {
    if (
      isLoaderIntersecting &&
      liveSessionsList?.length &&
      !previousLivesLoading &&
      currentPage + 1 < liveSessionsListNextPage &&
      !isLastLiveSessionsListPage
    ) {
      setCurrentPage(liveSessionsListNextPage - 1);
      onRequestNextPage();
    }
  }, [
    isLoaderIntersecting,
    currentPage,
    liveSessionsList,
    liveSessionsListNextPage,
    isLastLiveSessionsListPage,
  ]);

  return (
    <Grid gridRowGap={[5, 5, 5]} style={{ flexGrow: 1 }} marginBottom={isTeacher ? 8 : 6}>
      <GridItem gridRow={1} gridColumn="1 / span 8">
        <Text size="medium" fontWeight="medium" as="h2" textAlign="left">
          <FormattedMessage id="live_sessions.previous_lives.title" />
        </Text>
      </GridItem>
      <GridItem gridRow={2} gridColumn="1 / span 8">
        <When condition={!!previousLivesList.length}>
          {() => (
            <>
              <Grid
                gridRowGap={4}
                gridColumnGap={4}
                gridTemplateColumns={["1fr", "repeat(2, 0.5fr)"]}
              >
                {previousLivesList.map((cardProps, index) => (
                  <GridItem
                    key={`previous-live-card-${getRoundIndex(index)}`}
                    gridRow={[getRoundIndex(index), Math.ceil(getRoundIndex(index) / 2)]}
                    gridColumn={[1, (index % 2) + 1]}
                  >
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <DomestikaSimpleLiveCard {...cardProps} width="100%" height="100%" />
                  </GridItem>
                ))}
              </Grid>
              <When condition={!isLastLiveSessionsListPage && !isInitialDataLoading()}>
                {() => (
                  <div ref={loaderRef}>
                    <Flex padding={4} justifyContent="center">
                      <Loader size="medium" paddingBottom={3} />
                    </Flex>
                  </div>
                )}
              </When>
            </>
          )}
        </When>
        <When condition={!previousLivesList.length && isTeacher}>
          {() => (
            <EmptySectionPlaceholder
              message={intl.formatMessage({ id: "live_sessions.previous_lives.body" })}
            >
              <When condition={!hasActiveSession}>
                {() => (
                  <Button
                    size="none"
                    buttonType="text"
                    action={onScheduleNew}
                    marginTop={[3, 4, 4]}
                  >
                    <Text size="micro" fontWeight="medium" color="rambutan">
                      <FormattedMessage id="live_sessions.previous_lives.cta" />
                    </Text>
                    <IconForm marginLeft={2} color="rambutan">
                      <ArrowRight />
                    </IconForm>
                  </Button>
                )}
              </When>
            </EmptySectionPlaceholder>
          )}
        </When>
      </GridItem>
    </Grid>
  );
};

export default PreviousLives;
